<template>
  <VisionModal
    :key="props.customisedProduct.sku"
    :class="bem({'theme-promo': hasPromoTheme})"
    :open="props.promoSelectorOpen"
    :hide-close-button="true"
    :close-on-outside-click="false"
    @close="emits('toggle-promo-selector', false)"
  >
    <div :class="bem('wrapper')">
      <div
        v-if="props.customisedProduct?.product?.promotionProductGroup?.giftSelectorTitle"
        :class="bem('header')"
      >
        <VisionHeading tag="h3">
          {{ props.customisedProduct?.product?.promotionProductGroup?.giftSelectorTitle }}
        </VisionHeading>
      </div>
      <div :class="bem('body')">
        <div
          v-if="loading"
        >
          <div :class="bem('loading', { mobile: true })">
            <div
              class="vision-shimmer"
              :style="{ width: '100%', height: '200px', marginBottom: '8px' }"
            />
          </div>
          <div :class="bem('loading', { desktop: true })">
            <div
              class="vision-shimmer"
              :style="{ width: '100%', height: '320px', marginBottom: '12px' }"
            />
          </div>
          <div
            class="vision-shimmer"
            :style="{ width: '100%', height: '12px', marginBottom: '20px' }"
          />
          <div
            class="vision-shimmer"
            :style="{ width: '100%', height: '60px', marginBottom: '12px' }"
          />
          <div
            class="vision-shimmer"
            :style="{ width: '140px', height: '32px', marginLeft: 'auto', marginRight: 'auto' }"
          />
        </div>
        <div
          v-if="!loading && loadError"
          :class="bem('load-error')"
        >
          <p :class="bem('load-error-title')">
            There was an error fetching the promotion for this product.
          </p>
          <VisionButton
            v-if="!tryAgainDisabled"
            :disabled="tryingAgain"
            :button-text="tryingAgain ? 'Trying...' : 'Try again'"
            :outline="true"
            :background-colour="hasPromoTheme ? 'white' : 'black'"
            size="xx-small"
            @click="handleTryAgain"
          />
          <p
            v-else
            :class="bem('load-error-subtitle')"
          >
            Please try again later.
          </p>
        </div>
        <template v-if="!loading && !loadError">
          <VisionSlidoTabs
            direction="grid"
            :selected-index="0"
          >
            <template
              v-for="(customisedDiscountProduct, index) in customisedDiscountProducts"
              :key="index"
              #[buildKey(index)]
            >
              <VisionSlidoTab
                :selected="customisedDiscountProduct.sku === selectedDiscountSku"
                :disabled="addingProduct"
                :theme="hasPromoTheme ? 'promo' : 'default'"
                @selected="handleSelection(customisedDiscountProduct.sku)"
              >
                <PromoSelectorModalItem
                  :customised-discount-product="customisedDiscountProduct"
                  :promo-theme="hasPromoTheme"
                  :selected="
                    customisedDiscountProduct.sku === selectedDiscountSku ||
                      customisedDiscountProduct.state.preselectedSku === customisedDiscountProduct.sku
                  "
                />
              </VisionSlidoTab>
            </template>
            <template
              v-for="i in Math.max(0, (2 - customisedDiscountProducts.length))"
              :key="`sold-out-${i}`"
              #[buildSoldOutKey(i)]
            >
              <VisionSlidoTab
                :selected="false"
                :disabled="true"
                :theme="hasPromoTheme ? 'promo' : 'default'"
              >
                <PromoSelectorModalItem
                  :customised-discount-product="null"
                  :promo-theme="hasPromoTheme"
                  :selected="false"
                  :sold-out="true"
                />
              </VisionSlidoTab>
            </template>
          </VisionSlidoTabs>
          <p
            v-if="props.customisedProduct?.product?.promotionProductGroup?.disclaimer"
            :class="bem('disclaimer')"
          >
            <span v-if="!loading">
              {{ props.customisedProduct?.product?.promotionProductGroup?.disclaimer }}
            </span>
            <span
              v-else
              class="vision-shimmer"
              :style="{ display: 'block', width: '100%', height: '12px' }"
            />
          </p>
          <p
            v-if="props.customisedProduct?.product?.promotionProductGroup?.additionalMessage"
            :class="bem('additional-message')"
          >
            <span v-if="!loading">
              {{ props.customisedProduct?.product?.promotionProductGroup?.additionalMessage }}
            </span>
            <span
              v-else
              class="vision-shimmer"
              :style="{ display: 'block', width: '100%', height: '12px' }"
            />
          </p>
        </template>
      </div>
      <div :class="bem('footer')">
        <div
          v-if="progressError"
          :class="bem('error')"
        >
          {{ progressError }}
        </div>
        <template v-if="!loading && !loadError">
          <div :class="bem('primary-cta')">
            <VisionButton
              :disabled="addingProduct"
              button-text="Add Free Gift To Bag"
              background-colour="gradient"
              size="large"
              :promo-button="hasPromoTheme && hasPromoButton"
              @click="handleAddGift"
            />
          </div>
          <div :class="bem('secondary-cta')">
            <VisionLink
              button-text="Skip"
              :line-link="true"
              :is-button="true"
              theme="grey"
              @click="handleSkip"
            >
              Continue Without Gift
            </VisionLink>
          </div>
        </template>
        <template v-else-if="!loading && loadError">
          <div :class="bem('primary-cta')">
            <VisionButton
              button-text="Close"
              :background-colour="hasPromoTheme ? 'white' : 'black'"
              @click="emits('toggle-promo-selector', false)"
            />
          </div>
        </template>
      </div>
    </div>
  </VisionModal>
</template>

<script setup lang="ts">
import { buildCartItems, getFreshStockQuantities, buildQtyChangePayload, type Bundle } from '~/assets/js/helpers/shopify'
import type CustomisedProduct from '~/models/CustomisedProduct'

const props = withDefaults(defineProps<{
  promoSelectorOpen: boolean,
  customisedProduct: any,
  preSelectedDiscountSku?: string
  primarySkuAddedToCart: {
    sku: string,
    baseSku: string
  }
  changeGift?: boolean
}>(), {
  promoSelectorOpen: false,
  customisedProduct: {},
  preSelectedDiscountSku: '',
  changeGift: false
})

const emits = defineEmits(['toggle-promo-selector'])

const bem = useBEM('PromoSelectorModal')
const shopifyStore = useShopifyStore()
const promoStore = usePromoStore()
const { $product, $sentry } = useNuxtApp()

const addingProduct = ref(false)
const progressError = ref('')
const loadError = ref('')
const selectedDiscountSku = ref(props.preSelectedDiscountSku)
const customisedDiscountProducts = ref([])
const loading = ref(false)
const tryingAgain = ref(false)
const tryAgainCount = ref(0)
const giftDiscounts = ref(shopifyStore.getDiscountsFromProduct(props.customisedProduct))
const { $track } = useNuxtApp()

function buildKey(index: number) {
  return 'option-' + index
}

function buildSoldOutKey(index: number) {
  return 'option-' + (index + customisedDiscountProducts?.value.length)
}

function handleSelection(selectedSku: string) {
  selectedDiscountSku.value = selectedSku
}

async function handleTryAgain() {
  if (tryAgainDisabled.value) {
    return
  }

  tryAgainCount.value++
  tryingAgain.value = true

  try {
    await shopifyStore.getAutomaticDiscounts({ refresh: true })

    await fetchAndPopulateProducts()

    if (customisedDiscountProducts.value?.length) {
      loadError.value = ''
      tryAgainCount.value = 0
    }
  } catch(e) {
    $sentry.captureException(e)
  }

  loading.value = false
  tryingAgain.value = false
}

function getGiftAlreadyInCart(): Bundle | null | undefined {
  return Object.values(shopifyStore.bundledCartItems).find((bundleItem: Bundle) => {
    return bundleItem.summary.bundleParentSku === props.customisedProduct.sku
  })
}

async function addFreeGift () {
  try {
    await shopifyStore.addCartItems(buildCartItems(selectedCustomisedDiscountProduct.value))

    addingProduct.value = false
    shopifyStore.toggleCart(true)

    await $track.giftSelector.addGiftToCart(selectedCustomisedDiscountProduct.value)

    emits('toggle-promo-selector', false)
  } catch(e) {
    addingProduct.value = false
    progressError.value = 'Could not add free gift'

    $sentry.captureException(e)
  }
}

async function incrementExistingGift (selectedGiftAlreadyInCart: Bundle) {
  try {
    addingProduct.value = true

    await shopifyStore.updateCartItems(buildQtyChangePayload(selectedGiftAlreadyInCart, selectedGiftAlreadyInCart.summary.bundleQuantity + 1))

    await $track.giftSelector.incrementGift(selectedGiftAlreadyInCart)

    addingProduct.value = false
  } catch(e) {
    addingProduct.value = false
    progressError.value = 'Could not update free gift quantity'

    $sentry.captureException(e)
  }
}

async function swapGift (giftAlreadyInCart: Bundle) {
  try {
    addingProduct.value = true

    if (!giftAlreadyInCart.summary.bundleSku) {
      throw new Error('Cannot remove existing item to swap gifts as no SKU is defined.')
    }

    await shopifyStore.removeCartItems([ giftAlreadyInCart.summary.bundleSku ])

    await shopifyStore.addCartItems(buildCartItems(selectedCustomisedDiscountProduct.value, [], giftAlreadyInCart.summary.bundleQuantity))

    await $track.giftSelector.swapGift(selectedCustomisedDiscountProduct.value)
  } catch(e) {
    addingProduct.value = false
    progressError.value = 'Could not update free gift quantity'

    $sentry.captureException(e)
  }
}

async function handleAddGift() {
  if (!selectedDiscountSku.value) {
    progressError.value = "Select a gift or skip"
    return
  }

  addingProduct.value = true

  const giftAlreadyInCart = getGiftAlreadyInCart()

  /**
   * Customer is adding a product to cart that has no existing free gift in cart and
   * no request to change the product has been made.
   */
  if(!giftAlreadyInCart && !props.changeGift) {
    await addFreeGift()
  }

  /**
   * Customer has requested to change the gift product but selected the same gift,
   * close modal and do nothing.
   */
  else if (giftAlreadyInCart && selectedCustomisedDiscountProduct.value.sku === giftAlreadyInCart.summary.bundleSku && props.changeGift) {
    emits('toggle-promo-selector', false)
    return
  }

  /**
   * Customer has requested to change gift and their new selection does not match
   * the existing selection in cart. Remove existing gift product and add new product.
   */
  else if (giftAlreadyInCart && selectedCustomisedDiscountProduct.value.sku !== giftAlreadyInCart.summary.bundleSku && props.changeGift) {
    await swapGift(giftAlreadyInCart)
  }

  /**
   * Customer is adding an additional gift product, increment existing gift by one.
   */
  else if (giftAlreadyInCart && selectedCustomisedDiscountProduct.value.sku === giftAlreadyInCart.summary.bundleSku && !props.changeGift) {
    await incrementExistingGift(giftAlreadyInCart)
  }
}

async function handleSkip() {
  if (props.changeGift) {
    const giftAlreadyInCart = getGiftAlreadyInCart()

    if (giftAlreadyInCart) {
      await shopifyStore.removeCartItems([ giftAlreadyInCart.summary.bundleSku ])
      await $track.giftSelector.removeGift(giftAlreadyInCart)
      return
    }
  } else {
    await $track.giftSelector.skipped()
    emits('toggle-promo-selector', false)
  }
}

async function getStockFromIds(commerceIds: Array<string>) {
  const response = await $fetch('/api/' + useRoute().params.locale + '/stock', {
    method: 'POST',
    body: {
      commerceIds: commerceIds.map((id: string) => `id:${id}`).join(' OR '),
    }
  })
  return response
}

function debugPromoSelector (productData, freshStockResult) {
  const consoleTableData: any = []
  productData.forEach((customisedProduct) => {
    const quantities = getFreshStockQuantities(customisedProduct, freshStockResult)

    let quantitiesBreakdown = ''
    
    quantities.forEach(quantity => {
      quantitiesBreakdown += '[SKU: ' + quantity.sku + ', Stock: ' +  quantity.quantity + '] '
    })

    consoleTableData.push({
      'SKU': customisedProduct.sku,
      'Display order (Dato)': customisedProduct.state.displayOrder,
      'Group ID (Dato)': customisedProduct.state.groupId,
      'Lowest Quantity Part (Live Stock)': quantities[0].sku + ': ' + quantities[0].quantity,
      'Buffer Threshold (Dato)': customisedProduct.state.stockBufferLimit,
      'Below Threshold': quantities[0].quantity < customisedProduct.state.stockBufferLimit,
      'All Part Qunaities (Live Stock)': quantitiesBreakdown
    })
  })

  console.table(consoleTableData)
}

async function filterProductsWithStock(productData: any) {
  const allProductsCommerceIds: Array<string> = Array.from(
    new Set(productData.flatMap((customisedDiscountProduct: any) => {
      if (customisedDiscountProduct.isLimitedEdition
        || customisedDiscountProduct.sku === customisedDiscountProduct.product.sku
      ) {
        return customisedDiscountProduct.product.commerceId.split('/').pop() || ''
      }

      if(customisedDiscountProduct.isPrebuiltVariant) {
        return customisedDiscountProduct?.matchingPrebuiltVariant?.commerceId?.split('/')?.pop() || ''
      } else {
        if (Object.entries(customisedDiscountProduct.choices).length) {
          return (Object.values(customisedDiscountProduct.choices).map(choice => {
            return choice?.commerceId?.split('/')?.pop() || ''
          }))
        }
      }
    })))

  const freshStockResult = await getStockFromIds(allProductsCommerceIds)

  if (useRoute().query.debugPromoSelector) {
    debugPromoSelector(productData, freshStockResult)
  }

  return productData.filter(customisedProduct => {
    const quantities = getFreshStockQuantities(customisedProduct, freshStockResult)

    if (!quantities.length) return false

    return quantities[0].quantity > customisedProduct.state.stockBufferLimit
  })
}

async function fetchAndPopulateProducts() {
  if (!allVisibleDiscountOptions.value?.length) {
    emits('toggle-promo-selector', false)
    throw new Error('There are no visible discount options to choose from. Auto-closing selector.')
  }

  const productData: Array<any> = (await Promise.allSettled(
    allVisibleDiscountOptions.value.map((visisbleDiscountOption) => {
      return $product.getCustomisedProduct(visisbleDiscountOption.baseSku, {
        sku: visisbleDiscountOption.sku,
        useLitePackaging: visisbleDiscountOption.litePackaging,
        stockBufferLimit: visisbleDiscountOption.stockQtyBuffer,
        groupId: visisbleDiscountOption.groupId,
        ...visisbleDiscountOption
      })
    })
  )).map(result =>
    result.status === 'fulfilled' && result.value? result.value : null
  )
  .filter(a => a)


  productData.filter((product: any) => {
    return product.allVisibleChoicesMade || product.isInStock
  })

  const productsWithStock = await filterProductsWithStock(productData)

  customisedDiscountProducts.value = productsWithStock.reduce((acc, curr) => {
    const currGroupId = curr.state.groupId ?? 1

    if (!acc[currGroupId]) acc[currGroupId] = []
    acc[currGroupId].push(curr)

    return acc
  }, []).flatMap(
    (products: null | Array<typeof CustomisedProduct>) => (products || []).sort(
      (a: typeof CustomisedProduct, b: typeof CustomisedProduct) => a.state.displayOrder - b.state.displayOrder
    )?.[0]
  ).filter(Boolean)

  if (productData?.length && !customisedDiscountProducts.value?.length) {
    $sentry.captureException('No products left in stock. Promo selector modal showing nothing.')
    emits('toggle-promo-selector', false)
  }

  if (!props.preSelectedDiscountSku) {
    selectedDiscountSku.value = customisedDiscountProducts.value[0].sku
  }
}

const tryAgainDisabled = computed(() => {
  return tryAgainCount.value >= 3 && !tryingAgain.value
})

const selectedCustomisedDiscountProduct = computed(() => {
  const matchedProduct: any = customisedDiscountProducts.value.find((customisedDiscountProduct: any) => {
    return customisedDiscountProduct.sku === selectedDiscountSku.value
  })

  if(props.primarySkuAddedToCart && matchedProduct) {
    matchedProduct.state.primarySkuAddedToCart = props.primarySkuAddedToCart
  }

  return matchedProduct
})

const discountOptionsDato = computed(()=> {
  return (props.customisedProduct?.product?.promotionProductGroup?.giftedProduct || []).map((discountOptionDato, index) => {
    return {
      ...discountOptionDato,
      displayOrder: index + 1
    }
  })
})

const allAvailableDiscountOptionsShopify = computed(() => {
  return giftDiscounts.value?.allDiscountOptions
})

const allVisibleDiscountOptions = computed(() => {
  if(!discountOptionsDato.value.length) {
    return []
  }

  const matchedDiscounts = allAvailableDiscountOptionsShopify.value.map(discountOptionShopify => {
    const matchedDiscountOptionDato = discountOptionsDato.value.find(discountOptionDato => {
      // If a full product sku match (e.g. limited editions)
      if (discountOptionDato.sku === discountOptionShopify.sku) {
        return true
      }

      // Else split the Dato SKU and search through each part for a match in allAvailableDiscountOptionsShopify
      const datoSkuParts = discountOptionDato.sku.split('-')
      if (datoSkuParts.length) {
        return !!datoSkuParts.find(datoSkuPart => {
          return datoSkuPart === discountOptionShopify.sku
        })
      }

      return false
    })

    if (matchedDiscountOptionDato && discountOptionShopify.thresholdSummary.thresholdForDiscountMet) {
      return {
        ...discountOptionShopify,
        ...matchedDiscountOptionDato
      }
    }
  }).filter(a => a)

  return [
    ...new Map(
      matchedDiscounts.map((matchedDiscount: any) => [matchedDiscount.sku, matchedDiscount])
    ).values()
  ]
})

const hasPromoTheme = computed(() => {
  return allVisibleDiscountOptions.value.some(discount => discount.discountSummary.specialTheme)
})

const hasPromoButton = computed(() => {
  // Make sure the promo theme has colours
  return !!promoStore.theme?.keyColour
})

onMounted(async () => {
  loading.value = true

  try {
    await fetchAndPopulateProducts()
  } catch(e) {
    loadError.value = 'Could not get products at this time'
    $sentry.captureException(e)
  }

  await $track.giftSelector.opened({
    productsShown: customisedDiscountProducts.value.length
  })

  loading.value = false
})

watch(selectedDiscountSku, async (newSkuValue) => {
  if(newSkuValue) {
    progressError.value = ''

    await $track.giftSelector.selectedGift(selectedCustomisedDiscountProduct)
  }
})

watch(progressError, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      progressError.value = ''
    }, 3000)
  }
})

watch(loadError, (newValue) => {
  if(newValue) {
    $sentry.captureException('Could not display items in Promo Selector Modal.')
  }
})

watch(() => props.promoSelectorOpen, async (newValue) => {
  if (!newValue) {
    loadError.value = ''
    loading.value = false
    tryAgainCount.value = 0
    tryingAgain.value = false
  }
})

watch(() => props.customisedProduct.sku, async () => {
  loading.value = true

  giftDiscounts.value = shopifyStore.getDiscountsFromProduct(props.customisedProduct)

  try {
    await fetchAndPopulateProducts()
  } catch(e) {
    loadError.value = 'Could not get products at this time'
    $sentry.captureException(e)
  }

  loading.value = false
})
</script>

<style lang="postcss" scoped>
.PromoSelectorModal {
  @screen md {
    --vision-modal-max-height: calc(100vh - 8px);
    --vision-modal-max-width: 35rem;
  }
}

.PromoSelectorModal__loading--mobile {
  @apply block md:hidden;
}

.PromoSelectorModal__loading--desktop {
  @apply hidden md:block;
}

.PromoSelectorModal__wrapper {
  @apply px-4 py-6 md:px-8;
}

.PromoSelectorModal__header {
  @apply mb-4 text-center;
}

.PromoSelectorModal__footer {
  @apply flex flex-col items-center;
}

.PromoSelectorModal__error {
  @apply bg-red absolute text-white font-bold -translate-y-full w-full text-center py-2 text-xs;
}

.PromoSelectorModal__disclaimer {
  @apply text-[10px] text-grey-500 mt-2;
}

.PromoSelectorModal__additional-message {
  @apply mt-3 text-sm text-grey text-center;
}

.PromoSelectorModal__primary-cta {
  @apply flex flex-col mt-6 mb-4 max-w-lg w-full;
}

.PromoSelectorModal__secondary-cta {
  @apply flex justify-center text-sm;
}

.PromoSelectorModal__load-error {
  @apply bg-grey-light rounded-md py-40 px-4 text-center;
}

.PromoSelectorModal__load-error-title {
  @apply mb-3;
}

.PromoSelectorModal__load-error-subtitle {
  @apply text-xs;
}

/** Modifiers
  --theme
*/

.PromoSelectorModal--theme-promo {
  --vision-slido-bg-colour: var(--promo-bg-tint);
  --vision-slido-tab-active-background-color: var(--promo-bg);
  --vision-slido-border-colour: var(--promo-bg-tint);
  --shimmer-bg-color: var(--promo-bg-tint);
  --shimmer-darker-color: var(--promo-mid-bg);
  @apply border;
  background: var(--promo-bg, theme('colors.white'));
  border: 1px solid var(--promo-mid-bg, theme('colors.white'));

  .PromoSelectorModal__header {
    --promo-gradient-percent: 75%;
    @apply promo-t-gradient;
  }

  .PromoSelectorModal__load-error {
    @apply text-white;
    background-color: var(--promo-bg-tint, theme('colors.grey-light'));
  }

  .PromoSelectorModal__additional-message {
    color: var(--promo-key, theme('colors.black'));
  }
}
</style>